<template>
  <div
    class="product-listing flex flex-col items-center gap-10 justify-center grid lg:grid"
    data-testid="ProductListingProductCardsList"
  >
    <ProductCardHydratedOnVisible
      v-for="item in productsWithPositions"
      :key="item.objectID"
      data-testid="productListingProductCardsListItem"
      :title="item.name"
      :full-product-price="item.fullProductPrice"
      :discounted-price="item.discountedPrice"
      :last30-days-price="item.last30DaysPrice ?? undefined"
      :image="item.image"
      :link="item.url"
      :tags="item.tags"
      :brand="item.brand"
      :sku="item.objectID"
      :stock-count="item.stockCount"
      :rating="item.avgRating"
      :reviews-count="item.reviewsCount"
      :price-guarantee-url="priceGuaranteeUrl"
      :dimensions="getDimensions(item)"
      :is-freight="item.isPackageTypeFreight"
      :parcel-delivery-date="parcelDeliveryDate"
      :freight-delivery-date="freightDeliveryDate"
      :key-facts="attributesLocalized ? attributesLocalized.highlights : []"
      :release-date="item.releaseDate ?? ''"
      :energy-rating="item.energyRatingDetails"
      :currency="item.priceCurrency"
      :categories="item.categories"
      :product-box-type-tag="productBoxClickLocation"
      @product-clicked="() => onProductClicked(item)"
      @title-hovered="(productSku) => getProductAttributes(item.url, productSku)"
      @title-leave="() => cancelDataFetching()"
    />
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, hydrateOnVisible } from 'vue';
import type { Component } from 'vue';
import type { CtProductCard } from '~~/types/productListing/CtProductCard';
import type ProductDimensionsInterface from '~~/types/product/attribute/ProductDimensions';
import { addHitPositions } from '~~/helpers/productListing/addHitPositions';

let productCard: Component | undefined;

// Once loaded, the cards resolve before the next paint, so a newly mounted listing doesn't show
// its pagination with an empty grid first.
export const preloadProductCard = async (): Promise<Component> => {

  productCard ??= (await import('~/components/molecules/Product/ProductCard.vue')).default;

  return productCard;

};

// Defined once, so cards mounted after a filter change render together. <LazyProductCard hydrate-on-visible>
// wraps every card in new async components, so the new list filled in card by card.
const ProductCardHydratedOnVisible = defineAsyncComponent({
  loader: preloadProductCard,
  hydrate: hydrateOnVisible(),
});
</script>

<script setup lang="ts">
// CT version of ProductListing; products arrive already mapped to cards.
defineOptions({ name: 'CtProductListing' });

const props = withDefaults(defineProps<{
  products?: CtProductCard[];
  query?: string;
  offset?: number;
}>(), {
  products: () => [],
  query: '',
  offset: 0,
});

const emit = defineEmits<{
  (e: 'product-listing-shown', products: CtProductCard[]): void;
}>();

const { products, query } = toRefs(props);
const route = useRoute();
const { isOnSearchPage } = useIsPage();
const { callViewHitsEvent, callClickHitEvent } = useTypesenseGtmEvents();
const { priceGuaranteeUrl } = useRouteBestPriceGuarantee();
const { search: searchProductDeliveryDate, parcelDeliveryDate, freightDeliveryDate } = useProductDeliveryDate(true);
const currentSlug = Array.isArray(route.params.slug) ? route.params.slug[0] : (route.params.slug || '');
const { callViewItemListEvent } = useViewItemListEvent(products.value, {
  searchTerm: query.value, currentSlug,
});

onMounted(() => {

  emit('product-listing-shown', props.products);
  callViewItemListEvent();

  if (isOnSearchPage.value) {

    callViewHitsEvent(props.products);

  }

});

const productsWithPositions = computed(() => addHitPositions(props.products, props.offset));

const onProductClicked = (product: CtProductCard & { __position: number }) => {

  if (isOnSearchPage.value) {

    callClickHitEvent(product, props.query);

  }

};

const getDimensions = (item: CtProductCard): ProductDimensionsInterface => ({
  height: item.height,
  width: item.width,
  length: item.length,
  hasDimensions: item.hasDimensions,
});

const {
  search: searchProduct,
  masterProductLocalisedAttributes: attributesLocalized,
  masterProduct: product,
} = useProductForListing();

const timeout = ref<ReturnType<typeof setTimeout> | null>(null);

const getProductAttributes = (link: string, sku: string) => {

  if (product.value && product.value.sku === sku) {

    return;

  }

  const searchParams = { slug: link.slice(1) };

  clearTimeout(timeout.value ?? undefined);
  timeout.value = setTimeout(async () => {

    await searchProduct(searchParams);

  }, 300);

};

const cancelDataFetching = () => {

  clearTimeout(timeout.value ?? undefined);

};

const { productBoxClickLocation } = useProductBoxClickLocation();

useLazyAsyncData(`parcel-freight-delivery-date`, async () => {

  await searchProductDeliveryDate();

  return '';

});
</script>

<style lang="scss">
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;

.product-listing {
  grid-template-columns: repeat(auto-fill, var(--product-card-width));
  padding: 0 20px;
}
</style>
